import React, { useEffect, useState, useMemo, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { FileText, Lock, Search, Filter, ChevronUp, ChevronDown, MapPin, MoreVertical, Trash2, Archive, RotateCcw, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { SERVICES, serviceLabel } from "@/components/NewVisitModal";

export function StatusBadge({ v }) {
  if (v.archived) {
    return <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex items-center gap-1 w-fit"><EyeOff size={10} /> Archiviata</span>;
  }
  if (v.status === "closed") {
    if (v.report_sent_at) return <span className="px-2 py-0.5 rounded-full bg-brand-azure/15 text-brand-azure text-xs font-bold">Referto inviato</span>;
    return <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center gap-1 w-fit"><Lock size={10} /> Chiusa</span>;
  }
  return <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">Bozza</span>;
}

function SimpleConfirm({ open, message, onConfirm, onCancel, busy = false, testid = "simple-confirm" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid={testid} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <p className="text-base text-brand-navy font-semibold leading-snug" data-testid={`${testid}-msg`}>{message}</p>
        <div className="flex justify-end gap-2 mt-5">
          <button data-testid={`${testid}-cancel`} disabled={busy} onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-60">Annulla</button>
          <button data-testid={`${testid}-ok`} disabled={busy} onClick={onConfirm} className="px-5 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white disabled:opacity-60">Conferma</button>
        </div>
      </div>
    </div>
  );
}

function RowActionsMenu({ v, onOpen, onAction }) {
  const [open, setOpen] = useState(false);
  const isDraft = !v.archived && v.status !== "closed" && !v.closed_at && !v.report_sent_at;
  const isClosed = !v.archived && (v.status === "closed" || v.closed_at || v.report_sent_at);
  const isArchived = !!v.archived;
  return (
    <div className="relative inline-block text-left">
      <button
        data-testid={`row-menu-${v.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((x) => !x); }}
        className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 hover:text-brand-navy"
        title="Azioni"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-48 bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-40 py-1 text-left" data-testid={`row-menu-popup-${v.id}`}>
            <button onClick={() => { setOpen(false); onOpen(); }} className="w-full text-left px-3 py-2 text-sm font-bold text-brand-azure hover:bg-brand-azure/10">Apri</button>
            {isDraft && (
              <button data-testid={`row-delete-${v.id}`} onClick={() => { setOpen(false); onAction("delete"); }} className="w-full text-left px-3 py-2 text-sm font-bold text-red-600 hover:bg-red-50 flex items-center gap-2">
                <Trash2 size={13} /> Elimina bozza
              </button>
            )}
            {isClosed && (
              <button data-testid={`row-archive-${v.id}`} onClick={() => { setOpen(false); onAction("archive"); }} className="w-full text-left px-3 py-2 text-sm font-bold text-red-600 hover:bg-red-50 flex items-center gap-2">
                <Archive size={13} /> Archivia referto
              </button>
            )}
            {isArchived && (
              <button data-testid={`row-restore-${v.id}`} onClick={() => { setOpen(false); onAction("restore"); }} className="w-full text-left px-3 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50 flex items-center gap-2">
                <RotateCcw size={13} /> Ripristina
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FiltersBar({ filters, setFilters, expanded, setExpanded, sites = [] }) {
  const clear = () => setFilters({ q: "", name: "", surname: "", fiscal_code: "", birth: "", phone: "", email: "", service: "", status_: "", date_from: "", date_to: "", report: "", sede_id: "" });
  const active = Object.values(filters).some((v) => v);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input data-testid="filter-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Cerca nome, cognome, CF, telefono, email…" className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure" />
        </div>
        <button data-testid="toggle-filters" onClick={() => setExpanded(!expanded)} className={`px-4 py-2.5 rounded-lg border-2 font-bold text-sm flex items-center gap-2 transition ${expanded || active ? "border-brand-azure text-brand-azure" : "border-[#E2E8F0] text-slate-600 hover:border-brand-azure"}`}>
          <Filter size={14} /> Filtri {active && <span className="bg-brand-azure text-white rounded-full w-5 h-5 text-[10px] flex items-center justify-center">{Object.values(filters).filter(Boolean).length}</span>}
        </button>
        {active && <button data-testid="filter-clear" onClick={clear} className="text-sm text-slate-500 hover:text-red-600 font-semibold">Pulisci</button>}
      </div>
      {expanded && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-[#E2E8F0]" data-testid="filters-grid">
          <div>
            <label className="text-xs font-bold text-slate-500">Nome</label>
            <input data-testid="filter-name" value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Cognome</label>
            <input data-testid="filter-surname" value={filters.surname} onChange={(e) => setFilters({ ...filters, surname: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Data nascita</label>
            <input type="date" data-testid="filter-birth" value={filters.birth} onChange={(e) => setFilters({ ...filters, birth: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Codice fiscale</label>
            <input data-testid="filter-cf" value={filters.fiscal_code} onChange={(e) => setFilters({ ...filters, fiscal_code: e.target.value.toUpperCase() })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm uppercase" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Telefono</label>
            <input data-testid="filter-phone" value={filters.phone} onChange={(e) => setFilters({ ...filters, phone: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Email</label>
            <input data-testid="filter-email" value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Tipologia</label>
            <select data-testid="filter-service" value={filters.service} onChange={(e) => setFilters({ ...filters, service: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm">
              <option value="">Tutte</option>
              {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Stato</label>
            <select data-testid="filter-status" value={filters.status_} onChange={(e) => setFilters({ ...filters, status_: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm">
              <option value="">Tutti</option>
              <option value="draft">Bozza</option>
              <option value="closed">Chiusa</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Data da</label>
            <input type="date" data-testid="filter-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Data a</label>
            <input type="date" data-testid="filter-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Referto</label>
            <select data-testid="filter-report" value={filters.report} onChange={(e) => setFilters({ ...filters, report: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm">
              <option value="">Tutti</option>
              <option value="sent">Solo inviati</option>
            </select>
          </div>
          {sites.length > 0 && (
            <div>
              <label className="text-xs font-bold text-slate-500 flex items-center gap-1"><MapPin size={11} /> Sede</label>
              <select data-testid="filter-sede" value={filters.sede_id} onChange={(e) => setFilters({ ...filters, sede_id: e.target.value })} className="mt-1 w-full px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm bg-white">
                <option value="">Tutte</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function VisitsHistoryTable() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [filters, setFilters] = useState({ q: "", name: "", surname: "", fiscal_code: "", birth: "", phone: "", email: "", service: "", status_: "", date_from: "", date_to: "", report: "", sede_id: "" });
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [showArchived, setShowArchived] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/medico/sites").then(({ data }) => setSites((data || []).filter((s) => s.active !== false))).catch(() => setSites([]));
  }, []);

  const sitesById = useMemo(() => {
    const m = {};
    sites.forEach((s) => { m[s.id] = s; });
    return m;
  }, [sites]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (showArchived) params.include_archived = true;
      const { data } = await api.get("/visits", { params });
      setList(data);
    } catch (e) { toast.error(apiErr(e, "Errore")); }
    finally { setLoading(false); }
  }, [filters, showArchived]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const askConfirm = (visit, action) => setConfirmState({ visit, action, busy: false });
  const doAction = async () => {
    if (!confirmState) return;
    const { visit, action } = confirmState;
    setConfirmState((s) => ({ ...s, busy: true }));
    try {
      if (action === "delete") {
        await api.delete(`/visits/${visit.id}`);
        toast.success("Bozza eliminata");
      } else if (action === "archive") {
        await api.post(`/visits/${visit.id}/cancel`, {});
        toast.success("Referto archiviato");
      } else if (action === "restore") {
        await api.post(`/visits/${visit.id}/restore`, {});
        toast.success("Visita ripristinata");
      }
      setConfirmState(null);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Operazione non riuscita"));
      setConfirmState((s) => ({ ...s, busy: false }));
    }
  };

  const confirmMessage = useMemo(() => {
    if (!confirmState) return "";
    if (confirmState.action === "delete") return "Stai eliminando la bozza. Sicuro di voler procedere?";
    if (confirmState.action === "archive") return "Stai archiviando il referto. Sicuro di voler procedere?";
    if (confirmState.action === "restore") return "Stai ripristinando questa visita. Sicuro di voler procedere?";
    return "";
  }, [confirmState]);

  const sorted = useMemo(() => {
    const arr = [...list];
    arr.sort((a, b) => {
      const av = (a[sortKey] ?? "") + "";
      const bv = (b[sortKey] ?? "") + "";
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [list, sortKey, sortDir]);

  const toggleSort = (k) => { if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } };
  const SortIcon = ({ k }) => sortKey !== k ? null : (sortDir === "asc" ? <ChevronUp size={12} className="inline" /> : <ChevronDown size={12} className="inline" />);

  return (
    <div data-testid="visits-history-section">
      <FiltersBar filters={filters} setFilters={setFilters} expanded={expanded} setExpanded={setExpanded} sites={sites} />
      <div className="flex items-center justify-end gap-2 mb-2">
        <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer select-none" data-testid="toggle-show-archived">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded border-slate-300"
            data-testid="toggle-show-archived-input"
          />
          Mostra annullate / archiviate
        </label>
      </div>

      {loading ? <div className="text-slate-500 py-8 text-center">Caricamento…</div> : sorted.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center">
          <FileText className="mx-auto text-slate-300 mb-3" size={36} />
          <div className="font-bold text-brand-navy">Nessuna visita trovata</div>
          <div className="text-sm text-slate-500">Crea una nuova visita o modifica i filtri.</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-x-auto" data-testid="visits-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
              <tr>
                <th onClick={() => toggleSort("created_at")} className="text-left px-4 py-3 cursor-pointer hover:text-brand-navy">Data <SortIcon k="created_at" /></th>
                <th onClick={() => toggleSort("patient_surname")} className="text-left px-4 py-3 cursor-pointer hover:text-brand-navy">Cognome <SortIcon k="patient_surname" /></th>
                <th onClick={() => toggleSort("patient_name")} className="text-left px-4 py-3 cursor-pointer hover:text-brand-navy">Nome <SortIcon k="patient_name" /></th>
                <th className="text-left px-4 py-3">Nascita</th>
                <th className="text-left px-4 py-3">Codice fiscale</th>
                <th onClick={() => toggleSort("service")} className="text-left px-4 py-3 cursor-pointer hover:text-brand-navy">Tipo <SortIcon k="service" /></th>
                <th className="text-left px-4 py-3">Sede</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3">Aggiornata</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((v) => (
                <tr key={v.id} data-testid={`visit-row-${v.id}`} className="border-t border-[#E2E8F0] hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{new Date(v.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-4 py-2.5 font-bold text-brand-navy">{v.patient_surname}</td>
                  <td className="px-4 py-2.5">{v.patient_name}</td>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{v.patient_birth || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{v.patient_fiscal_code || "—"}</td>
                  <td className="px-4 py-2.5">{serviceLabel(v.service)}</td>
                  <td className="px-4 py-2.5">
                    {v.sede_id && (sitesById[v.sede_id] || v.sede_snapshot) ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold" data-testid={`visit-sede-${v.id}`}>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: (sitesById[v.sede_id]?.color || v.sede_snapshot?.color || "#2C4667") }}></span>
                        <span className="text-slate-700">{sitesById[v.sede_id]?.name || v.sede_snapshot?.name || "—"}</span>
                      </span>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge v={v} /></td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{new Date(v.updated_at || v.created_at).toLocaleString("it-IT")}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`open-visit-${v.id}`} onClick={() => nav(`/visite/${v.id}`)} className="text-brand-azure hover:bg-brand-azure/10 px-3 py-1.5 rounded-lg font-bold text-xs">Apri</button>
                      <RowActionsMenu v={v} onOpen={() => nav(`/visite/${v.id}`)} onAction={(act) => askConfirm(v, act)} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-t border-[#E2E8F0]">{sorted.length} visit{sorted.length === 1 ? "a" : "e"}</div>
        </div>
      )}

      <SimpleConfirm
        open={!!confirmState}
        message={confirmMessage}
        busy={confirmState?.busy}
        onConfirm={doAction}
        onCancel={() => !confirmState?.busy && setConfirmState(null)}
        testid="visit-action-confirm"
      />
    </div>
  );
}
